import type { FileType } from './file.types';
import type {
  WireChat,
  WireChatMessage,
  WireRagContextResponse,
} from '@circuit/api';

// ===== FILE ATTACHMENT TYPES =====

export type ChatFileAttachmentStatus = 'loading' | 'success' | 'error';

export interface ChatFileAttachment {
  id: string;
  file?: File;
  name: string;
  type: string;
  size: number;
  base64Data?: string; // Data URI for newly uploaded files (e.g., "data:image/png;base64,...")
  documentUrl?: string; // Document URL for stored attachments
  status: ChatFileAttachmentStatus;
  error?: string;
}

export type FileUploadErrorType = 'size' | 'type' | 'count' | 'processing';

export interface FileUploadError {
  type: FileUploadErrorType;
  message: string;
  fileName?: string;
}

// ===== RAW EVENT TYPES =====

export type TextChunk = string;

export interface ErrorChunk {
  message: string;
  /** Classified error type from the backend (e.g. "overloaded", "rate_limited", "output_limit_exceeded") */
  error_type?: string;
  /** Whether the client should retry this request */
  retryable?: boolean;
}

export interface CanceledChunk {
  message?: string;
  /**
   * Why the turn was canceled (mirrors `wire.CanceledReason`).
   * - `user_requested` — the user clicked cancel; terminal CANCELLED.
   * - `shutdown` — the server (pod) shut down mid-stream (SIGTERM). The BE
   *   persisted the partial response with an in-message "Interrupted" marker,
   *   so the FE finalizes it like any other terminal (FINALIZED, not a false
   *   CANCELLED), flagging `serverShutdown` (telemetry and the "Interrupted" chat status).
   */
  reason?: 'user_requested' | 'shutdown';
}

export interface WarningChunk {
  message: string;
}

export interface ReferenceChunkMetadata {
  friendly_name?: string;
  name?: string;
  source?: string;
  value?: string | number | object;
}

export interface ReferenceStartChunk {
  reference_id: string;
}

type ReferenceDocumentType = (typeof FileType)[keyof typeof FileType];

export interface ReferenceChunk {
  chunk_id?: string;
  circuit_id?: string;
  content?: string;
  document_display_name?: string;
  document_id?: string;
  document_name?: string;
  document_summary?: string;
  document_type?: ReferenceDocumentType;
  metadata?: ReferenceChunkMetadata[];
  reference_id?: string;
  score?: number;
}

export interface ReferenceGroupChunk {
  reference_ids: string[];
}

export interface DebugChunk {
  path?: string;
  prompt?: string;
  prompt_version?: number;
  context_limit?: number;
  context_results?: WireRagContextResponse[];
  timings?: Record<string, number>;
  metrics?: Record<string, number>;
  override_system_prompt?: string;
}

export interface ImageChunk {
  base64_data?: string;
  format?: string;
  filename?: string;
  alt_text?: string;
  description?: string;
  circuit_id?: string;
  document_id?: string;
}

export interface ArtifactFile {
  file_path: string;
  circuit_id: string;
  document_id: string;
}

export interface ArtifactChunk {
  files: ArtifactFile[];
  title: string;
}

/**
 * Payload for an `a2ui` inference event — exactly ONE A2UI protocol message
 * (spec v0.9.x, a2ui.org) for a surface rendered natively in chat:
 * `createSurface`, `updateComponents`, `updateDataModel`, or `deleteSurface`.
 * The stream of events for a surface is fed, in order, into that surface's
 * A2UI message processor on the rendering side.
 *
 * `event_id` is a server-stamped idempotency key: the same event re-presented
 * (live stream vs persisted history, refetches) carries the same id, so
 * consumers can accumulate surface state exactly once across message
 * boundaries. `surface_id` duplicates the envelope's surfaceId so events
 * route to their surface without parsing the message payload.
 */
export interface A2UIChunk {
  event_id?: string;
  surface_id: string;
  tool_use_id: string;
  /**
   * Where the client presents the surface. Only present on `createSurface`
   * events; absent means `transcript`.
   */
  surface_type?: A2UISurfaceType;
  /** Optional short title for the surface (modal pill / tab label). */
  surface_title?: string;
  /** One verbatim A2UI spec envelope, e.g. `{version: 'v0.9', createSurface: {...}}`. */
  message: Record<string, unknown>;
}

/**
 * `transcript` renders inline in the message; `modal` renders in the compose
 * dock and blocks input until OK'd or cancelled; `tab` is hosted in a
 * non-closable folder tab in App Layer tab-view (inline elsewhere).
 */
export type A2UISurfaceType = 'transcript' | 'modal' | 'tab';

export type ProgressChunk = string;

export interface ToolUsageChunk {
  parent_tool_use_id?: string | null;
  tool_use_id: string;
  name: string;
  message: string;
  is_error?: boolean;
}

export interface StartChunk {
  // Stream initialization event - contains format version
  format_version?: number;
}

export interface MsgIdsChunk {
  // Message ID tracking event - contains user and assistant message identifiers
  user_message_id?: string;
  assistant_message_id?: string;
}

/**
 * Minimal shape of a client-tool-use block delivered on a `paused` event.
 * Mirrors the wire `WireAgenticContentBlockToolUse` but redeclared here so
 * `@circuit/chat-utils` (the protocol package) doesn't depend on the
 * generated `@circuit/api` types. Consumers structurally-assign wire values.
 */
export interface PausedToolUseBlock {
  input?: Record<string, unknown>;
  is_server_side?: boolean;
  name?: string;
  tool_use_id: string;
}

/**
 * Data payload of a `paused` streaming event. The BE emits this as the
 * terminal frame when the LLM pauses waiting on client-side tool execution
 * (e.g. AskUserQuestion). `reason` currently only takes the literal
 * `"pending_tool_use"`; widened to `string` so unknown future reasons
 * don't fail decoding on the wire.
 */
export interface PausedChunk {
  reason: string;
  tool_use_blocks?: PausedToolUseBlock[];
}

export interface StreamingEventData {
  start: StartChunk;
  debug: DebugChunk;
  error: ErrorChunk;
  canceled: CanceledChunk | undefined;
  warning: WarningChunk;
  image: ImageChunk;
  artifact: ArtifactChunk;
  a2ui: A2UIChunk;
  progress: ProgressChunk;
  ref: ReferenceChunk;
  ref_group: ReferenceGroupChunk;
  ref_start: ReferenceStartChunk;
  text: TextChunk;
  tool_usage_progress: ToolUsageChunk;
  tool_usage_start: ToolUsageChunk;
  tool_usage_stop: ToolUsageChunk;
  msg_ids: MsgIdsChunk;
  paused: PausedChunk;
}

export type StreamingEventCollection = {
  [K in keyof StreamingEventData]: Array<StreamingEventData[K]>;
};

export interface StreamingEvent<
  TEventType extends keyof StreamingEventData = keyof StreamingEventData,
> {
  event: TEventType;
  data: TEventType extends keyof StreamingEventData
    ? StreamingEventData[TEventType]
    : unknown;
}

export interface ChatMessage {
  id: WireChatMessage['id'];
  chatId: WireChatMessage['chat_id'];
  role: WireChatMessage['role'];
  source?: WireChatMessage['source'];
  events: StreamingEvent[];
  files?: ChatFileAttachment[];
  sourceMetadata?: WireChatMessage['source_metadata'];
  userId?: WireChatMessage['user_id'];
  createdAt?: WireChatMessage['created_at'];
}

export type Chat = WireChat;

export type TodoStatus = 'pending' | 'in_progress' | 'completed';

export interface TodoItem {
  content: string;
  status: TodoStatus;
}

// ===== TYPE GUARDS =====

export function isTextEvent(
  event: StreamingEvent,
): event is StreamingEvent<'text'> {
  return event.event === 'text';
}

export function isErrorEvent(
  event: StreamingEvent,
): event is StreamingEvent<'error'> {
  return event.event === 'error';
}

export function isCanceledEvent(
  event: StreamingEvent,
): event is StreamingEvent<'canceled'> {
  return event.event === 'canceled';
}

export function isWarningEvent(
  event: StreamingEvent,
): event is StreamingEvent<'warning'> {
  return event.event === 'warning';
}

export function isReferenceStartEvent(
  event: StreamingEvent,
): event is StreamingEvent<'ref_start'> {
  return event.event === 'ref_start';
}

export function isReferenceEvent(
  event: StreamingEvent,
): event is StreamingEvent<'ref'> {
  return event.event === 'ref';
}

export function isReferenceGroupEvent(
  event: StreamingEvent,
): event is StreamingEvent<'ref_group'> {
  return event.event === 'ref_group';
}

export function isDebugEvent(
  event: StreamingEvent,
): event is StreamingEvent<'debug'> {
  return event.event === 'debug';
}

export function isProgressEvent(
  event: StreamingEvent,
): event is StreamingEvent<'progress'> {
  return event.event === 'progress';
}

export function isImageEvent(
  event: StreamingEvent,
): event is StreamingEvent<'image'> {
  return event.event === 'image';
}

export function isToolUsageStartEvent(
  event: StreamingEvent,
): event is StreamingEvent<'tool_usage_start'> {
  return event.event === 'tool_usage_start';
}

export function isToolUsageProgressEvent(
  event: StreamingEvent,
): event is StreamingEvent<'tool_usage_progress'> {
  return event.event === 'tool_usage_progress';
}

export function isToolUsageStopEvent(
  event: StreamingEvent,
): event is StreamingEvent<'tool_usage_stop'> {
  return event.event === 'tool_usage_stop';
}

export function isArtifactEvent(
  event: StreamingEvent,
): event is StreamingEvent<'artifact'> {
  return event.event === 'artifact';
}

export function isA2UIEvent(
  event: StreamingEvent,
): event is StreamingEvent<'a2ui'> {
  return event.event === 'a2ui';
}
